import {
  BrowserCollectionCoordinator,
  createBrowserWASQLitePersistence,
  persistedCollectionOptions,
  type BrowserWASQLiteDatabase,
} from '@tanstack/browser-db-sqlite-persistence'
import { BTreeIndex, type SyncConfig } from '@tanstack/db'
import { electricCollectionOptions } from '@tanstack/electric-db-collection'
import { queryCollectionOptions } from '@tanstack/query-db-collection'
import { createCollection } from '@tanstack/vue-db'
import { formatDistanceToNow, formatISO, startOfMonth, subMonths } from 'date-fns'
import { de } from 'date-fns/locale'
import { z } from 'zod'
import { IssueHistoryMetadataSchema, parseIssueHistoryMetadata } from '@/Types/issueJson'
import { bulkyWasteOrderTypes } from './lib/bulkyWasteOrderType'
import {
  openReadablePersistedDatabase,
  resetCorruptPersistedDatabaseOnce,
  resetPersistedDatabaseFromUrl,
  resetPersistedDatabaseOnSchemaChange,
} from './persistence'
import { queryClient } from './utils/queryClient'
import { parseEwkb } from './utils/wkbParser'
import axios from 'axios'
import { deferredSync } from './lib/deferredSync'
import { persistedCollectionId } from './persistenceSchema'
import { persistenceStatus, startStartupMeasure } from './lib/startupPerformance'

/**
 * Restores the auto-indexing behavior that was the default before @tanstack/db 0.6
 * (where it flipped from `eager` to `off`). Without this, every `useLiveQuery` join
 * logs "Join requires an index ... Falling back to loading all data" and degrades to a
 * full scan. Eager indexing only builds an index when a collection is actually queried
 * with a simple where/join expression, so collections read in full incur no cost.
 */
const autoIndexOptions = {
  autoIndex: 'eager',
  defaultIndexType: BTreeIndex,
} as const

/**
 * Existing cache epoch. Keep this stable: changing it resets the complete local database,
 * including stream offsets. Collection imports must not wait for this storage to open.
 */
const PERSISTED_SCHEMA_VERSION = 17

let databasePromise: Promise<BrowserWASQLiteDatabase | null> | undefined
const persistence = createBrowserWASQLitePersistence({
  database: {
    execute: async <TRow>(sql: string, params?: readonly unknown[]) => {
      const database = await getDatabase()
      if (!database) throw new Error('OPFS persistence is unavailable')
      return database.execute<TRow>(sql, params)
    },
  },
  coordinator: new BrowserCollectionCoordinator({ dbName: 'meldeportal' }),
})

function getDatabase(): Promise<BrowserWASQLiteDatabase | null> {
  return (databasePromise ??= initializePersistence())
}

async function initializePersistence(): Promise<BrowserWASQLiteDatabase | null> {
  persistenceStatus.value = 'opening'
  const finish = startStartupMeasure('persistence:initialize')
  try {
    await resetPersistedDatabaseFromUrl()
    await resetPersistedDatabaseOnSchemaChange(PERSISTED_SCHEMA_VERSION)
    const database = await openReadablePersistedDatabase()
    persistenceStatus.value = 'ready'
    return database
  } catch (error) {
    persistenceStatus.value = 'unavailable'
    if (!(await resetCorruptPersistedDatabaseOnce(error))) {
      console.error(
        'OPFS persistence unavailable; collections will run without a local cache',
        error,
      )
    }
    return null
  } finally {
    finish()
  }
}

/**
 * Wait at sync startup, not module import, so Vue can render while storage opens.
 * All persisted collections share one opening attempt and the same fallback decision.
 */
function persisted<T extends object>(options: T): T {
  // Preserve the adapter-specific schema and utils types at this generic registry boundary.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const config = options as T & { sync: SyncConfig; id: string }
  const id = persistedCollectionId(config.id)
  // Keep the adapter's ownership claim on the options passed to createCollection.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const persistedConfig = persistedCollectionOptions({
    ...options,
    id,
    persistence,
    schemaVersion: PERSISTED_SCHEMA_VERSION,
  } as never)
  return {
    ...options,
    ...persistedConfig,
    sync: deferredSync(config.sync, async () => {
      if (!(await getDatabase())) return config.sync
      const finish = startStartupMeasure(`collection:${config.id}:initial-ready`)
      const finishRows = startStartupMeasure(`collection:${config.id}:first-rows`)
      return {
        ...persistedConfig.sync,
        sync: (params) => {
          let measured = false
          let receivedRows = false
          let measuredRows = false
          return persistedConfig.sync.sync({
            ...params,
            write: (message) => {
              receivedRows = true
              params.write(message)
            },
            commit: (signal) => {
              const applied = params.commit(signal)
              if (receivedRows && !measuredRows) {
                measuredRows = true
                if (applied === true) finishRows()
                else void applied.then(finishRows).catch(() => undefined)
              }
              return applied
            },
            markReady: () => {
              if (!measured) {
                measured = true
                finish()
              }
              params.markReady()
            },
          })
        },
      }
    }),
  }
}

/**
 * Fetches a `/tanstack-api/` resource with the shared credentials and error handling every
 * collection's queryFn used to repeat. `response.json()` is `any`, so the single unavoidable
 * boundary assertion lives here instead of at every call site. `label` defaults to `resource`
 * for the error message; pass it when the two differ.
 */
async function fetchTanstackApi<T>(resource: string, label: string = resource): Promise<T> {
  const response = await fetch(`/tanstack-api/${resource}`, {
    credentials: 'same-origin',
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch ${label}: ${response.statusText}`)
  }
  // Fetch exposes parsed JSON as `any`; callers supply the endpoint response type.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return response.json() as Promise<T>
}

// Zod schema definitions for type safety
const MediaItemSchema = z.object({
  id: z.string(),
  filename: z.string(),
  path: z.string(),
  type: z.string(),
  size: z.number(),
  created_at: z.string(),
})

const MediaAnnotationSchema = z.object({
  id: z.string(),
  type: z.enum(['blur']),
  source: z.enum(['manual', 'ai_model']),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  detector: z.string().nullable().optional(),
})

export const MediaAnnotationsEntrySchema = z.object({
  derived_path: z.string(),
  rendered_at: z.string().optional(),
  annotations: z.array(MediaAnnotationSchema),
})

const LocationGeojsonSchema = z.object({
  type: z.literal('Point'),
  coordinates: z.tuple([z.number(), z.number()]),
})

const IssueSchema = z.object({
  id: z.number(),
  description: z.string().nullable(),
  extended_description: z.string().nullable(),
  street: z.string().nullable(),
  house_number: z.string().nullable(),
  postal_code: z.string().nullable(),
  city: z.string().nullable(),
  status: z.enum([
    'reported',
    'acknowledged',
    'in_progress',
    'partially_resolved',
    'resolved',
    'rejected',
    'duplicate',
    'forwarded',
  ]),
  duplicate_of_issue_id: z.number().nullable(),
  image_classification_status: z
    .enum([
      'pending',
      'checking_nsfw',
      'is_not_safe_for_work',
      'blurring',
      'categorizing',
      'determining_attributes',
      'suggesting_workflow',
      'completed',
      'failed',
    ])
    .nullable(),
  anonymization_status: z.enum(['pending', 'processing', 'completed', 'failed']).nullable(),
  reported_at: z.string(),
  acknowledged_at: z.string().nullable(),
  started_at: z.string().nullable(),
  resolved_at: z.string().nullable(),
  rejected_at: z.string().nullable(),
  created_at: z.string(),
  issue_type_id: z.number().nullable(),
  suggested_issue_type_id: z.number().nullable(),
  department_id: z.number().nullable(),
  location: LocationGeojsonSchema, // Parsed from EWKB; issues.location is NOT NULL at the DB
  localization_hint: z.string().nullable(),
  due_at: z.string().nullable(),
  reporter_type: z.enum(['App\\Models\\User', 'App\\Models\\Guest']).nullable(),
  reporter_id: z.number().nullable(),
  created_by_id: z.number().nullable(),
  created_by_kind: z.enum(['user', 'datafleet_sync', 'flic', 'sap_sync']),
  vehicle_id: z.number().nullable(),
  reporter_user_type: z
    .enum([
      '009 – MP interner User',
      '010 – MP externer User',
      '000 – MP unbekannter User',
      '001 – Telefon',
      '002 – E-Mail',
      '003 – Fax',
      '004 – Anrufbeantworter',
      '005 – Brief',
      '006 – Besucher',
      '008 – Mängelmelder',
      '011 – DataFleet',
      '012 – Flic',
    ])
    .nullable(),
  media_json: z.array(MediaItemSchema),
  media_annotations_json: z.record(z.string(), MediaAnnotationsEntrySchema),
  photo_angle: z.number().nullable(),
  address_locked: z.boolean(),
})

const IssueTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  short_code: z.string(),
  description: z.string().nullable(),
  selectable_by_reporters: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
})

const PolygonGeojsonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(z.array(z.number()))),
})

const MultiPolygonGeojsonSchema = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: z.array(z.array(z.array(z.array(z.number())))),
})

const ZoneBoundarySchema = z.union([PolygonGeojsonSchema, MultiPolygonGeojsonSchema])

const ZoneSchema = z.object({
  id: z.number(),
  name: z.string(),
  color: z.string(),
  is_locked: z.boolean(),
  zone_group_id: z.string().nullable(),
  sort_order: z.string(),
  boundary: ZoneBoundarySchema.nullable(),
  active_from: z.string().nullable(),
  active_until: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

const ZoneGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  parent_id: z.string().nullable(),
  sort_order: z.string(),
  flag_overlapping_children: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
})

const VehicleSchema = z.object({
  id: z.number(),
  plate: z.string(),
})

const IssueAttributeSchema = z.object({
  id: z.number(),
  issue_type_id: z.number(),
  slug: z.string(),
  label: z.string(),
  description: z.string(),
  data_type: z.enum(['checkbox', 'number']),
  required: z.boolean(),
  setter_role_ids: z.array(z.number().int()),
  min_value: z.number().nullable(),
  max_value: z.number().nullable(),
  step: z.number().nullable(),
  order: z.number(),
  active_from: z.string().nullable(),
  active_until: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

const IssueAttributeValueSchema = z.object({
  id: z.number(),
  issue_id: z.number(),
  issue_attribute_id: z.number(),
  value_json: z.unknown(),
  source: z.string(),
  corrected_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

const UserSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
})

const GuestSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
})

const IssueHistorySchema = z.object({
  id: z.number(),
  issue_id: z.number(),
  actor_id: z.number().nullable(),
  actor_type: z.enum(['user', 'integration', 'bot', 'workflow_action', 'automation']),
  integration_name: z.string().nullable(),
  bot_name: z.string().nullable(),
  workflow_id: z.number().nullable(),
  automation_id: z.number().nullable(),
  event_type: z.string(),
  visibility: z.enum(['internal', 'external']),
  from_status: z.string().nullable(),
  to_status: z.string().nullable(),
  from_issue_type_id: z.number().nullable(),
  to_issue_type_id: z.number().nullable(),
  from_department_id: z.number().nullable(),
  to_department_id: z.number().nullable(),
  from_workflow_id: z.number().nullable(),
  to_workflow_id: z.number().nullable(),
  from_tracemate_task_id: z.number().nullable(),
  to_tracemate_task_id: z.number().nullable(),
  from_description: z.string().nullable(),
  to_description: z.string().nullable(),
  from_street: z.string().nullable(),
  to_street: z.string().nullable(),
  from_house_number: z.string().nullable(),
  to_house_number: z.string().nullable(),
  from_postal_code: z.string().nullable(),
  to_postal_code: z.string().nullable(),
  from_city: z.string().nullable(),
  to_city: z.string().nullable(),
  from_localization_hint: z.string().nullable(),
  to_localization_hint: z.string().nullable(),
  from_due_at: z.string().nullable(),
  to_due_at: z.string().nullable(),
  metadata: z.preprocess(parseIssueHistoryMetadata, IssueHistoryMetadataSchema.nullable()),
  created_at: z.string(),
  updated_at: z.string(),
})

const UsedTemplateSchema = z.object({
  id: z.number(),
  name: z.string(),
})

const IssueCommentSchema = z.object({
  id: z.number(),
  issue_id: z.number(),
  author_id: z.number().nullable(),
  visibility: z.enum(['internal', 'external']),
  body_json: z.unknown(),
  body_text: z.string(),
  used_templates: z.array(UsedTemplateSchema).nullable(),
  media_json: z.array(MediaItemSchema),
  extended_description: z.string().nullable().default(null),
  edited_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

const IssueCommentTemplateSchema = z.object({
  id: z.number(),
  name: z.string(),
  body_json: z.unknown(),
  body_text: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
})

const TracemateTaskSchema = z.object({
  id: z.number(),
  tracemate_id: z.number().nullable(),
  tracemate_syncer_id: z.number().nullable(),
  tracemate_task_template_id: z.string().nullable(),
  issue_id: z.number().nullable(),
  task_id: z.number().nullable(),
  tour_type: z.string().nullable(),
  category: z.string().nullable(),
  vehicle: z.string().nullable(),
  plan_date: z.string().nullable(),
  execution_date: z.string().nullable(),
  sync_status: z.string(),
  delivery_status: z.enum(['pending', 'processing', 'completed', 'dead_letter']).nullable(),
  delivery_attempts: z.number().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

const TracemateTaskWithLocationSchema = z.object({
  id: z.number(),
  issue_id: z.number().nullable(),
  tour_type: z.string().nullable(),
  category: z.string().nullable(),
  vehicle: z.string().nullable(),
  plan_date: z.string().nullable(),
  execution_date: z.string().nullable(),
  location: LocationGeojsonSchema.nullable(), // Parsed from EWKB
})

function getMonthsAgoDate(months: number): string {
  return formatISO(subMonths(new Date(), months), { representation: 'date' })
}

function getStartOfMonthMonthsAgoDate(months: number): string {
  return formatISO(startOfMonth(subMonths(new Date(), months)), { representation: 'date' })
}

const BulkyWasteAppointmentSchema = z.object({
  id: z.number(),
  issue_id: z.number().nullable(),
  task_id: z.number().nullable(),
  sap_id: z.string().nullable(),
  address: z.string(),
  post_code: z.string().nullable(),
  city: z.string().nullable(),
  applicant_first_name: z.string().nullable(),
  applicant_last_name: z.string().nullable(),
  applicant_email: z.string().nullable(),
  route: z.string().nullable(),
  order_text: z.string().nullable(),
  type: z.enum(bulkyWasteOrderTypes),
  status: z.enum(['pending', 'booked', 'cancelled']),
  cancellation_reason: z.string().nullable(),
  pickup_at: z.string(),
  // Time-of-day the collection was executed (SAP), scoped to the pickup_at date. A set
  // value confirms the pickup actually happened. Only sperrmuell is populated in practice.
  sperrmuell_execution_time: z.string().nullable(),
  eschrott_execution_time: z.string().nullable(),
  holz_execution_time: z.string().nullable(),
  ordered_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

const BulkyWasteAppointmentIndexSchema = BulkyWasteAppointmentSchema

// Base shape plus the EWKB-parsed `location` geometry. Used by the single on-demand
// bulkyWasteAppointmentsCollection that serves both the inbox detail view (where issue_id = ...)
// and the inbox map / duplicate detection (where pickup_at in an upcoming window).
const BulkyWasteAppointmentWithLocationSchema = BulkyWasteAppointmentSchema.extend({
  location: LocationGeojsonSchema.nullable(), // Parsed from EWKB
})

const BulkyWasteRouteSchema = z.object({
  id: z.number(),
  street: z.string(),
  housenumber: z.string(),
  postal_code: z.string(),
  city: z.string(),
  next_bulky_waste_pickup_date: z.string(),
  location_geojson: LocationGeojsonSchema,
})

const IssueForwardingSchema = z.object({
  id: z.number(),
  issue_id: z.number(),
  forwarded_by: z.number().nullable(),
  email: z.string(),
  name: z.string().nullable(),
  message: z.string().nullable(),
  status: z.enum(['pending', 'sent', 'failed']),
  notification_sent_at: z.string().nullable(),
  notification_failed_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

const TaskSchema = z.object({
  id: z.number(),
  issue_id: z.number(),
  assignee_id: z.number().nullable(),
  created_by_id: z.number().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']),
  due_at: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

const TaskHistoryBaseShape = {
  id: z.number(),
  task_id: z.number(),
  actor_id: z.number().nullable(),
  actor_type: z.enum(['user', 'integration', 'workflow_action', 'automation']),
  integration_name: z.string().nullable(),
  automation_id: z.number().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
} satisfies z.ZodRawShape

const TaskHistoryBaseSchema = z.object(TaskHistoryBaseShape)

const TaskHistoryCreatedSchema = z.object({
  ...TaskHistoryBaseShape,
  event_type: z.literal('created'),
  from_status: z.null(),
  to_status: z.null(),
})

const TaskHistoryStatusChangedSchema = z.object({
  ...TaskHistoryBaseShape,
  event_type: z.literal('status_changed'),
  from_status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']),
  to_status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']),
})

const TaskHistoryIntegrationEventSchema = z.object({
  ...TaskHistoryBaseShape,
  event_type: z.enum([
    'tracemate_task_created',
    'tracemate_task_synced',
    'tracemate_task_completed',
    'tracemate_task_deleted',
    'sap_appointment_created',
    'sap_appointment_synced',
  ]),
  from_status: z.null(),
  to_status: z.null(),
})

const TaskHistorySchema = z.discriminatedUnion('event_type', [
  TaskHistoryCreatedSchema,
  TaskHistoryStatusChangedSchema,
  TaskHistoryIntegrationEventSchema,
])

const AutomationExecutionSchema = z.object({
  id: z.number(),
  automation_id: z.number(),
  issue_id: z.number().nullable(),
  triggered_by_user_id: z.number().nullable(),
  trigger_type: z.string(),
  trigger_context: z.unknown().nullable(),
  status: z.enum(['running', 'completed', 'partially_failed', 'failed', 'skipped']),
  error_message: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

const ExternalIssueTypeSchema = z.object({
  id: z.number(),
  external_id: z.number(),
  name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
})

const ExternalIssueSchema = z.object({
  id: z.number(),
  external_id: z.number(),
  external_issue_type_id: z.number(),
  location: LocationGeojsonSchema.nullable(),
  description: z.string().nullable(),
  state: z.enum(['OPEN', 'IN_PROCESS', 'CLOSED', 'NOT_RESPONSIBLE']),
  image_urls: z.array(z.string()).nullable(),
  reported_at: z.string(),
})

const DatafleetIssueSchema = z.object({
  id: z.number(),
  image_id: z.string(),
  detected_at: z.string(),
  category: z.string(),
  status: z.string().nullable(),
  vehicle_license_plate: z.string().nullable(),
  related_issue_id: z.number().nullable(),
  datafleet_syncer_id: z.number().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

const DatafleetSyncerSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
})

// Type definitions from schemas
export type MediaItem = z.infer<typeof MediaItemSchema>
export type Issue = z.infer<typeof IssueSchema>
export type IssueType = z.infer<typeof IssueTypeSchema>
export type Zone = z.infer<typeof ZoneSchema>
export type ZoneGroup = z.infer<typeof ZoneGroupSchema>
export type Vehicle = z.infer<typeof VehicleSchema>
export type IssueAttribute = z.infer<typeof IssueAttributeSchema>
export type IssueAttributeValue = z.infer<typeof IssueAttributeValueSchema>
export type User = z.infer<typeof UserSchema>
export type Guest = z.infer<typeof GuestSchema>
export type IssueHistory = z.infer<typeof IssueHistorySchema>
export type IssueComment = z.infer<typeof IssueCommentSchema>
export type IssueCommentTemplate = z.infer<typeof IssueCommentTemplateSchema>
export type TracemateTask = z.infer<typeof TracemateTaskSchema>
export type TracemateTaskWithLocation = z.infer<typeof TracemateTaskWithLocationSchema>
export type BulkyWasteAppointment = z.infer<typeof BulkyWasteAppointmentSchema>
export type BulkyWasteAppointmentIndex = z.infer<typeof BulkyWasteAppointmentIndexSchema>
export type BulkyWasteRoute = z.infer<typeof BulkyWasteRouteSchema>
export type IssueForwarding = z.infer<typeof IssueForwardingSchema>
export type Task = z.infer<typeof TaskSchema>
export type TaskHistory = z.infer<typeof TaskHistorySchema>
export type TaskHistoryCreated = z.infer<typeof TaskHistoryCreatedSchema>
export type TaskHistoryStatusChanged = z.infer<typeof TaskHistoryStatusChangedSchema>
export type TaskHistoryWithActor = TaskHistory & {
  actor: { id: number; name: string } | null
  automation?: { id: number; name: string } | null
}
export type LocationGeojson = z.infer<typeof LocationGeojsonSchema>
export type AutomationExecution = z.infer<typeof AutomationExecutionSchema>
export type ExternalIssueType = z.infer<typeof ExternalIssueTypeSchema>
export type ExternalIssue = z.infer<typeof ExternalIssueSchema>
export type DatafleetIssue = z.infer<typeof DatafleetIssueSchema>
export type DatafleetSyncer = z.infer<typeof DatafleetSyncerSchema>
export interface SyncedIssue extends Issue {
  geojson: LocationGeojson
  address: string | null
  issue_type_name?: string | null
  reporter_name?: string | null
}

function getElectricUrl(): string {
  // When using Caddy proxy, Electric is served from /api/electric
  return new URL('/api/electric/v1/shape', globalThis.location.origin).href
}

export const http = axios.create({
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
})

export const issuesShapeParams = {
  table: 'issues',
  // Persistence can resume past a row it failed to store. A full replica makes the first later
  // update reconstruct that row instead of materializing only its changed columns.
  replica: 'full' as const,
  where:
    "deleted_at IS NULL AND (anonymization_status IS NULL OR anonymization_status IN ('completed', 'failed'))",
  columns: [
    'id',
    'description',
    'extended_description',
    'street',
    'house_number',
    'postal_code',
    'city',
    'status',
    'duplicate_of_issue_id',
    'image_classification_status',
    'anonymization_status',
    'reported_at',
    'acknowledged_at',
    'started_at',
    'resolved_at',
    'rejected_at',
    'created_at',
    'issue_type_id',
    'suggested_issue_type_id',
    'department_id',
    'location',
    'localization_hint',
    'due_at',
    'reporter_type',
    'reporter_id',
    'created_by_id',
    'created_by_kind',
    'vehicle_id',
    'reporter_user_type',
    'media_json',
    'media_annotations_json',
    'photo_angle',
    'address_locked',
  ],
}

// Create TanStack DB collections using Electric SQL sync
export function createIssuesElectricOptions(id: string) {
  return electricCollectionOptions({
    ...autoIndexOptions,
    id,
    shapeOptions: {
      url: getElectricUrl(),
      params: issuesShapeParams,
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
        date: (value) => value,
        numeric: (value) => {
          if (value === null || value === undefined) {
            return null
          }
          const parsed = Number(value)
          return Number.isNaN(parsed) ? null : parsed
        },
        // @ts-expect-error Type mismatch with geometry parser return type
        geometry: (value) => parseEwkb(value),
      },
    },
    getKey: (item) => item.id,
    schema: IssueSchema,
    onUpdate: async ({ transaction }) => {
      const txids = await Promise.all(
        transaction.mutations.map(async (mutation) => {
          const { original, changes } = mutation

          if (typeof original !== 'object' || original === null || !('id' in original)) {
            throw new Error('Issue update mutation missing identifier')
          }

          const { data } = await http.patch<{ txid: number }>(`/issues/${original.id}`, changes)
          return data.txid
        }),
      )

      const filteredTxids = txids.filter(
        (txid): txid is number => txid !== null && txid !== undefined,
      )

      if (filteredTxids.length === 0) {
        throw new Error('No valid txids returned from mutations')
      }

      const firstTxid = filteredTxids[0]
      if (firstTxid === undefined) {
        throw new Error('First txid is unexpectedly undefined')
      }

      return {
        txid: filteredTxids.length === 1 ? firstTxid : filteredTxids,
      }
    },
  })
}

export const issuesCollection = createCollection(
  persisted(createIssuesElectricOptions('tanstack-issues')),
)

// Pre-create indexes the overview/inbox joins and filters hit. The eager auto-indexer
// would build the same indexes on first query, but it logs a one-time size suggestion
// and does a full scan of ~20k rows while doing so.
issuesCollection.createIndex((row) => row.id)
issuesCollection.createIndex((row) => row.status)
issuesCollection.createIndex((row) => row.reporter_type)
issuesCollection.createIndex((row) => row.issue_type_id)
issuesCollection.createIndex((row) => row.reporter_id)
issuesCollection.createIndex((row) => row.created_by_id)

export function createIssueTypesQueryOptions(id: string) {
  return queryCollectionOptions({
    ...autoIndexOptions,
    id,
    queryKey: ['tanstack-api', 'issue_types'],
    queryFn: () => fetchTanstackApi<IssueType[]>('issue_types'),
    queryClient,
    getKey: (item) => item.id,
    schema: IssueTypeSchema,
    refetchInterval: 1000 * 15, // 15 seconds
  })
}

export const issueTypesCollection = createCollection(
  persisted(createIssueTypesQueryOptions('tanstack-issue-types')),
)

export const zonesCollection = createCollection(
  electricCollectionOptions({
    ...autoIndexOptions,
    id: 'tanstack-zones',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'zones',
        columns: [
          'id',
          'name',
          'color',
          'is_locked',
          'zone_group_id',
          'sort_order',
          'boundary',
          'active_from',
          'active_until',
          'created_at',
          'updated_at',
        ],
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
        // @ts-expect-error Type mismatch with geometry parser return type
        geometry: (value) => parseEwkb(value),
      },
    },
    getKey: (item) => item.id,
    schema: ZoneSchema,
    onInsert: async ({ transaction }) => {
      const txids = await Promise.all(
        transaction.mutations.map(async (mutation) => {
          const { modified } = mutation

          const { data } = await http.post<{ txid: number; id: number }>('/admin/zones', modified)
          return data.txid
        }),
      )

      const filteredTxids = txids.filter(
        (txid): txid is number => txid !== null && txid !== undefined,
      )

      if (filteredTxids.length === 0) {
        throw new Error('No valid txids returned from zone insert mutations')
      }

      const firstTxid = filteredTxids[0]
      if (firstTxid === undefined) {
        throw new Error('First txid is unexpectedly undefined')
      }

      return {
        txid: filteredTxids.length === 1 ? firstTxid : filteredTxids,
      }
    },
    onUpdate: async ({ transaction }) => {
      const txids = await Promise.all(
        transaction.mutations.map(async (mutation) => {
          const { original, changes } = mutation

          if (typeof original !== 'object' || original === null || !('id' in original)) {
            throw new Error('Zone update mutation missing identifier')
          }

          const { data } = await http.patch<{ txid: number }>(
            `/admin/zones/${original.id}/patch`,
            changes,
          )
          return data.txid
        }),
      )

      const filteredTxids = txids.filter(
        (txid): txid is number => txid !== null && txid !== undefined,
      )

      if (filteredTxids.length === 0) {
        throw new Error('No valid txids returned from mutations')
      }

      const firstTxid = filteredTxids[0]
      if (firstTxid === undefined) {
        throw new Error('First txid is unexpectedly undefined')
      }

      return {
        txid: filteredTxids.length === 1 ? firstTxid : filteredTxids,
      }
    },
    onDelete: async ({ transaction }) => {
      const txids = await Promise.all(
        transaction.mutations.map(async (mutation) => {
          const { original } = mutation

          if (typeof original !== 'object' || original === null || !('id' in original)) {
            throw new Error('Zone delete mutation missing identifier')
          }

          const { data } = await http.delete<{ txid: number }>(`/admin/zones/${original.id}`)
          return data.txid
        }),
      )

      const filteredTxids = txids.filter(
        (txid): txid is number => txid !== null && txid !== undefined,
      )

      if (filteredTxids.length === 0) {
        throw new Error('No valid txids returned from zone delete mutations')
      }

      const firstTxid = filteredTxids[0]
      if (firstTxid === undefined) {
        throw new Error('First txid is unexpectedly undefined')
      }

      return {
        txid: filteredTxids.length === 1 ? firstTxid : filteredTxids,
      }
    },
  }),
)

export const zoneGroupsCollection = createCollection(
  electricCollectionOptions({
    ...autoIndexOptions,
    id: 'tanstack-zone-groups',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'zone_groups',
        columns: [
          'id',
          'name',
          'parent_id',
          'sort_order',
          'flag_overlapping_children',
          'created_at',
          'updated_at',
        ],
      },
      parser: {
        timestamp: (value) => parseTimestamp(value),
      },
    },
    getKey: (item) => item.id,
    schema: ZoneGroupSchema,
    onInsert: async ({ transaction }) => {
      const txids = await Promise.all(
        transaction.mutations.map(async (mutation) => {
          const { modified } = mutation

          const { data } = await http.post<{ txid: number; id: number }>(
            '/admin/zone-groups',
            modified,
          )
          return data.txid
        }),
      )

      const filteredTxids = txids.filter(
        (txid): txid is number => txid !== null && txid !== undefined,
      )

      if (filteredTxids.length === 0) {
        throw new Error('No valid txids returned from zone group insert mutations')
      }

      const firstTxid = filteredTxids[0]
      if (firstTxid === undefined) {
        throw new Error('First txid is unexpectedly undefined')
      }

      return {
        txid: filteredTxids.length === 1 ? firstTxid : filteredTxids,
      }
    },
    onUpdate: async ({ transaction }) => {
      const txids = await Promise.all(
        transaction.mutations.map(async (mutation) => {
          const { original, changes } = mutation

          if (typeof original !== 'object' || original === null || !('id' in original)) {
            throw new Error('ZoneGroup update mutation missing identifier')
          }

          const { data } = await http.patch<{ txid: number }>(
            `/admin/zone-groups/${original.id}`,
            changes,
          )
          return data.txid
        }),
      )

      const filteredTxids = txids.filter(
        (txid): txid is number => txid !== null && txid !== undefined,
      )

      if (filteredTxids.length === 0) {
        throw new Error('No valid txids returned from mutations')
      }

      const firstTxid = filteredTxids[0]
      if (firstTxid === undefined) {
        throw new Error('First txid is unexpectedly undefined')
      }

      return {
        txid: filteredTxids.length === 1 ? firstTxid : filteredTxids,
      }
    },
    onDelete: async ({ transaction }) => {
      const txids = await Promise.all(
        transaction.mutations.map(async (mutation) => {
          const { original } = mutation

          if (typeof original !== 'object' || original === null || !('id' in original)) {
            throw new Error('ZoneGroup delete mutation missing identifier')
          }

          const { data } = await http.delete<{ txid: number }>(`/admin/zone-groups/${original.id}`)
          return data.txid
        }),
      )

      const filteredTxids = txids.filter(
        (txid): txid is number => txid !== null && txid !== undefined,
      )

      if (filteredTxids.length === 0) {
        throw new Error('No valid txids returned from zone group delete mutations')
      }

      const firstTxid = filteredTxids[0]
      if (firstTxid === undefined) {
        throw new Error('First txid is unexpectedly undefined')
      }

      return {
        txid: filteredTxids.length === 1 ? firstTxid : filteredTxids,
      }
    },
  }),
)

export const vehiclesCollection = createCollection(
  persisted(
    electricCollectionOptions({
      ...autoIndexOptions,
      id: 'tanstack-vehicles',
      shapeOptions: {
        url: getElectricUrl(),
        params: {
          table: 'vehicles',
          where: 'deleted_at IS NULL',
          columns: ['id', 'plate'],
        },
        parser: {
          int8: Number,
        },
      },
      getKey: (item) => item.id,
      schema: VehicleSchema,
    }),
  ),
)

export function createUsersQueryOptions(id: string) {
  return queryCollectionOptions({
    ...autoIndexOptions,
    id,
    queryKey: ['tanstack-api', 'users'],
    queryFn: () => fetchTanstackApi<User[]>('users'),
    queryClient,
    getKey: (item) => item.id,
    schema: UserSchema,
    refetchInterval: 1000 * 15, // 15 seconds
  })
}

export const usersCollection = createCollection(
  persisted(createUsersQueryOptions('tanstack-users')),
)

export function createGuestsQueryOptions(id: string) {
  return queryCollectionOptions({
    ...autoIndexOptions,
    id,
    queryKey: ['tanstack-api', 'guests'],
    queryFn: () => fetchTanstackApi<Guest[]>('guests'),
    queryClient,
    getKey: (item) => item.id,
    schema: GuestSchema,
    refetchInterval: 1000 * 15, // 15 seconds
  })
}

export const guestsCollection = createCollection(
  persisted(createGuestsQueryOptions('tanstack-guests')),
)

export function createIssueHistoriesElectricOptions(id: string) {
  return electricCollectionOptions({
    ...autoIndexOptions,
    id,
    // Detail-only: always read via a pushed-down `where(issue_id = ...)` predicate. On-demand
    // skips the eager full-table OPFS rehydrate at startup and lazily loads the selected issue's
    // subset instead, removing this collection from the inbox startup cost.
    syncMode: 'on-demand',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'issue_histories',
        columns: [
          'id',
          'issue_id',
          'actor_id',
          'actor_type',
          'integration_name',
          'bot_name',
          'workflow_id',
          'automation_id',
          'event_type',
          'visibility',
          'from_status',
          'to_status',
          'from_issue_type_id',
          'to_issue_type_id',
          'from_department_id',
          'to_department_id',
          'from_workflow_id',
          'to_workflow_id',
          'from_tracemate_task_id',
          'to_tracemate_task_id',
          'from_description',
          'to_description',
          'from_street',
          'to_street',
          'from_house_number',
          'to_house_number',
          'from_postal_code',
          'to_postal_code',
          'from_city',
          'to_city',
          'from_localization_hint',
          'to_localization_hint',
          'from_due_at',
          'to_due_at',
          'metadata',
          'created_at',
          'updated_at',
        ],
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
        date: (value) => value,
      },
    },
    getKey: (item) => item.id,
    schema: IssueHistorySchema,
  })
}

// Not persisted: on-demand collections never rehydrate from OPFS at startup (they lazily load the
// selected issue's subset from Electric on click), so persisting them buys nothing but adds an OPFS
// round-trip — worker contention plus a main-thread `receive` + re-insert on every issue switch.
export const issueHistoriesCollection = createCollection(
  createIssueHistoriesElectricOptions('tanstack-issue-histories'),
)

const externalIssueCommentsWhere = "visibility = 'external'"
const allIssueCommentsWhere = "visibility IN ('external', 'internal')"

interface PendingCommentInsert {
  clientMutationId: string
  photos: File[]
}

const pendingCommentInserts = new Map<number, PendingCommentInsert>()

export function attachPendingCommentInsert(
  tempId: number,
  clientMutationId: string,
  photos: File[],
): void {
  pendingCommentInserts.set(tempId, { clientMutationId, photos })
}

export function clearPendingCommentInsert(tempId: number): void {
  pendingCommentInserts.delete(tempId)
}

function commentInsertPayload(modified: IssueComment):
  | FormData
  | {
      body_json: IssueComment['body_json']
      client_mutation_id: string
      visibility: IssueComment['visibility']
      used_template_ids: number[] | null
    } {
  const usedTemplateIds = modified.used_templates?.map((template) => template.id) ?? null
  const pendingInsert = pendingCommentInserts.get(modified.id)
  pendingCommentInserts.delete(modified.id)

  if (pendingInsert === undefined) {
    throw new Error('Issue comment insert mutation missing client mutation ID')
  }

  if (pendingInsert.photos.length === 0) {
    return {
      body_json: modified.body_json,
      client_mutation_id: pendingInsert.clientMutationId,
      visibility: modified.visibility,
      used_template_ids: usedTemplateIds,
    }
  }

  const payload = new FormData()
  payload.append('body_json', JSON.stringify(modified.body_json))
  payload.append('client_mutation_id', pendingInsert.clientMutationId)
  payload.append('visibility', modified.visibility)
  payload.append('used_template_ids', JSON.stringify(usedTemplateIds ?? []))
  for (const photo of pendingInsert.photos) {
    payload.append('photos[]', photo)
  }

  return payload
}

export function createIssueCommentElectricOptions(id: string, includeInternal = false) {
  return electricCollectionOptions({
    ...autoIndexOptions,
    id,
    // Detail-only: always read via a pushed-down `where(issue_id = ...)` predicate; mutations only
    // ever target a currently-displayed (already-loaded) comment. On-demand skips the eager
    // full-table OPFS rehydrate at startup and lazily loads the selected issue's subset.
    syncMode: 'on-demand',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'issue_comments',
        // ForwardAuthController verifies this scope for users without internal issue access.
        where: includeInternal ? allIssueCommentsWhere : externalIssueCommentsWhere,
        columns: [
          'id',
          'issue_id',
          'author_id',
          'visibility',
          'body_json',
          'body_text',
          'used_templates',
          'media_json',
          'extended_description',
          'edited_at',
          'created_at',
          'updated_at',
        ],
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
      },
    },
    getKey: (item) => item.id,
    schema: IssueCommentSchema,
    onInsert: async ({ transaction }) => {
      const txids = await Promise.all(
        transaction.mutations.map(async (mutation) => {
          const { modified } = mutation

          if (typeof modified !== 'object' || modified === null || !('issue_id' in modified)) {
            throw new Error('Issue comment insert mutation missing issue_id')
          }

          const { data } = await http.post<{ id: number; txid: number }>(
            `/issues/${modified.issue_id}/comments`,
            commentInsertPayload(modified),
          )
          return data.txid
        }),
      )

      const filteredTxids = txids.filter(
        (txid): txid is number => txid !== null && txid !== undefined,
      )

      if (filteredTxids.length === 0) {
        throw new Error('No valid txids returned from comment insert mutations')
      }

      const firstTxid = filteredTxids[0]
      if (firstTxid === undefined) {
        throw new Error('First txid is unexpectedly undefined')
      }

      return {
        txid: filteredTxids.length === 1 ? firstTxid : filteredTxids,
      }
    },
    onUpdate: async ({ transaction }) => {
      const txids = await Promise.all(
        transaction.mutations.map(async (mutation) => {
          const { original, changes } = mutation

          if (
            typeof original !== 'object' ||
            original === null ||
            !('id' in original) ||
            !('issue_id' in original)
          ) {
            throw new Error('Issue comment update mutation missing identifier')
          }

          const { data } = await http.patch<{ txid: number }>(
            `/issues/${original.issue_id}/comments/${original.id}`,
            changes,
          )
          return data.txid
        }),
      )

      const filteredTxids = txids.filter(
        (txid): txid is number => txid !== null && txid !== undefined,
      )

      if (filteredTxids.length === 0) {
        throw new Error('No valid txids returned from comment update mutations')
      }

      const firstTxid = filteredTxids[0]
      if (firstTxid === undefined) {
        throw new Error('First txid is unexpectedly undefined')
      }

      return {
        txid: filteredTxids.length === 1 ? firstTxid : filteredTxids,
      }
    },
  })
}

// Not persisted — see issueHistoriesCollection (on-demand collections don't benefit from OPFS).
export const issueCommentsCollection = createCollection(
  createIssueCommentElectricOptions('tanstack-issue-comments'),
)

export const internalIssueCommentsCollection = createCollection(
  createIssueCommentElectricOptions('tanstack-issue-comments-internal', true),
)

export function createIssueAttributesQueryOptions(id: string) {
  return queryCollectionOptions({
    ...autoIndexOptions,
    id,
    queryKey: ['tanstack-api', 'issue_attributes'],
    queryFn: () => fetchTanstackApi<IssueAttribute[]>('issue_attributes'),
    queryClient,
    getKey: (item) => item.id,
    schema: IssueAttributeSchema,
    refetchInterval: 1000 * 15, // 15 seconds
  })
}

export const issueAttributesCollection = createCollection(
  persisted(createIssueAttributesQueryOptions('tanstack-issue-attributes')),
)

export function createIssueCommentTemplatesQueryOptions(id: string) {
  return queryCollectionOptions({
    ...autoIndexOptions,
    id,
    queryKey: ['issue-comment-templates'],
    queryFn: async () => {
      const { data } = await http.get<IssueCommentTemplate[]>('/issue-comment-templates')
      return data
    },
    queryClient,
    getKey: (item) => item.id,
    schema: IssueCommentTemplateSchema,
    onInsert: async ({ transaction }) => {
      await Promise.all(
        transaction.mutations.map(async ({ modified }) => {
          await http.post('/issue-comment-templates', {
            name: modified.name,
            body_json: modified.body_json,
          })
        }),
      )
    },
    onUpdate: async ({ transaction }) => {
      await Promise.all(
        transaction.mutations.map(async ({ original, modified }) => {
          await http.patch(`/issue-comment-templates/${original.id}`, {
            name: modified.name,
            body_json: modified.body_json,
          })
        }),
      )
    },
    onDelete: async ({ transaction }) => {
      await Promise.all(
        transaction.mutations.map(async ({ original }) => {
          await http.delete(`/issue-comment-templates/${original.id}`)
        }),
      )
    },
  })
}

// Not persisted: personal templates are tiny, user-scoped, and fetched on first use; the
// collection's query refetches after every mutation handler to pick up server state (ids).
export const issueCommentTemplatesCollection = createCollection(
  createIssueCommentTemplatesQueryOptions('tanstack-issue-comment-templates'),
)

export function createIssueAttributeValuesElectricOptions(id: string) {
  return electricCollectionOptions({
    ...autoIndexOptions,
    id,
    // Detail-only: read via a pushed-down `where(issue_id = ...)` predicate (EditShow) or as a
    // leftJoin lazy source keyed on issue_id (Inbox); values are saved server-side via router.post,
    // never mutated through the collection. On-demand skips the eager full-table OPFS rehydrate.
    syncMode: 'on-demand',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'issue_attribute_values',
        columns: [
          'id',
          'issue_id',
          'issue_attribute_id',
          'value_json',
          'source',
          'corrected_at',
          'created_at',
          'updated_at',
        ],
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
      },
    },
    getKey: (item) => item.id,
    schema: IssueAttributeValueSchema,
  })
}

// Not persisted — see issueHistoriesCollection (on-demand collections don't benefit from OPFS).
export const issueAttributeValuesCollection = createCollection(
  createIssueAttributeValuesElectricOptions('tanstack-issue-attribute-values'),
)
issueAttributeValuesCollection.createIndex((row) => row.issue_id)

export const tracemateTasksCollection = createCollection(
  queryCollectionOptions({
    ...autoIndexOptions,
    queryKey: ['tanstack-api', 'tracemate_tasks'],
    queryFn: () => fetchTanstackApi<TracemateTask[]>('tracemate_tasks'),
    queryClient,
    getKey: (item) => item.id,
    schema: TracemateTaskSchema,
    refetchInterval: 1000 * 15, // 15 seconds
  }),
)

export const tracemateTasksWithLocationCollection = createCollection(
  electricCollectionOptions({
    ...autoIndexOptions,
    id: 'tanstack-tracemate-tasks-with-location',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'tracemate_tasks',
        where: `issue_id IS NULL AND plan_date >= '${getMonthsAgoDate(1)}'`,
        columns: [
          'id',
          'issue_id',
          'tour_type',
          'category',
          'vehicle',
          'plan_date',
          'execution_date',
          'location',
        ],
      },
      parser: {
        int8: Number,
        // @ts-expect-error Type mismatch with geometry parser return type
        geometry: (value) => parseEwkb(value),
      },
    },
    getKey: (item) => item.id,
    schema: TracemateTaskWithLocationSchema,
  }),
)

// Single bulky-waste collection reused across the inbox: queried by `issue_id` for an issue's
// detail view and by a `pickup_at` window for the map + duplicate detection. On-demand + the EWKB
// `location` column mean each call site loads just the subset its predicate asks for (query-driven
// sync), instead of three narrow collections. Authorized by `viewAny Issue` in ForwardAuthController.
export const bulkyWasteAppointmentsCollection = createCollection(
  electricCollectionOptions({
    ...autoIndexOptions,
    id: 'tanstack-bulky-waste-appointments',
    syncMode: 'on-demand',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'bulky_waste_appointments',
        columns: [
          'id',
          'issue_id',
          'task_id',
          'sap_id',
          'address',
          'post_code',
          'city',
          'applicant_first_name',
          'applicant_last_name',
          'applicant_email',
          'route',
          'order_text',
          'type',
          'status',
          'cancellation_reason',
          'pickup_at',
          'sperrmuell_execution_time',
          'eschrott_execution_time',
          'holz_execution_time',
          'ordered_at',
          'created_at',
          'updated_at',
          'location',
        ],
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
        // @ts-expect-error Type mismatch with geometry parser return type
        geometry: (value) => parseEwkb(value),
      },
    },
    getKey: (item) => item.id,
    schema: BulkyWasteAppointmentWithLocationSchema,
  }),
)
bulkyWasteAppointmentsCollection.createIndex((row) => row.issue_id)

export function createBulkyWasteAppointmentsIndexCollection(syncOrderedFrom: string) {
  return createCollection(
    electricCollectionOptions({
      ...autoIndexOptions,
      id: `tanstack-bulky-waste-appointments-index-${syncOrderedFrom.replaceAll(/[^0-9]/g, '')}`,
      shapeOptions: {
        url: getElectricUrl(),
        params: {
          table: 'bulky_waste_appointments',
          where: `ordered_at >= '${syncOrderedFrom}' AND pickup_at IS NOT NULL`,
          columns: [
            'id',
            'issue_id',
            'task_id',
            'sap_id',
            'address',
            'post_code',
            'city',
            'applicant_first_name',
            'applicant_last_name',
            'applicant_email',
            'route',
            'order_text',
            'type',
            'status',
            'cancellation_reason',
            'pickup_at',
            'sperrmuell_execution_time',
            'eschrott_execution_time',
            'holz_execution_time',
            'ordered_at',
            'created_at',
            'updated_at',
          ],
        },
        parser: {
          int8: Number,
          timestamp: (value) => parseTimestamp(value),
        },
      },
      getKey: (item) => item.id,
      schema: BulkyWasteAppointmentIndexSchema,
    }),
  )
}

export const bulkyWasteSettlementCollectionRoutes = createCollection(
  queryCollectionOptions({
    ...autoIndexOptions,
    queryKey: ['tanstack-api', 'bulky_waste_routes', 'settlement_collection'],
    queryFn: () =>
      fetchTanstackApi<BulkyWasteRoute[]>(
        'bulky_waste_settlement_collection_routes',
        'bulky_waste_routes',
      ),
    queryClient,
    getKey: (item) => item.id,
    schema: BulkyWasteRouteSchema,
    refetchInterval: 1000 * 60 * 30, // 30 minutes
  }),
)

export const issueForwardingsCollection = createCollection(
  queryCollectionOptions({
    ...autoIndexOptions,
    queryKey: ['tanstack-api', 'issue_forwardings'],
    queryFn: () => fetchTanstackApi<IssueForwarding[]>('issue_forwardings'),
    queryClient,
    getKey: (item) => item.id,
    schema: IssueForwardingSchema,
    refetchInterval: 1000 * 15, // 15 seconds
  }),
)

export function createTasksElectricOptions(id: string) {
  return electricCollectionOptions({
    ...autoIndexOptions,
    id,
    // Detail-only: always read via a pushed-down `where(issue_id = ...)` predicate; mutations only
    // ever target a currently-displayed (already-loaded) task. On-demand skips the eager full-table
    // OPFS rehydrate at startup and lazily loads the selected issue's subset.
    syncMode: 'on-demand',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'tasks',
        where: 'deleted_at IS NULL',
        columns: [
          'id',
          'issue_id',
          'assignee_id',
          'created_by_id',
          'name',
          'description',
          'status',
          'due_at',
          'started_at',
          'completed_at',
          'created_at',
          'updated_at',
        ],
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
      },
    },
    getKey: (item) => item.id,
    schema: TaskSchema,
    onUpdate: async ({ transaction }) => {
      const txids = await Promise.all(
        transaction.mutations.map(async (mutation) => {
          const { original, changes } = mutation

          if (typeof original !== 'object' || original === null || !('id' in original)) {
            throw new Error('Task update mutation missing identifier')
          }

          const { data } = await http.patch<{ txid: number }>(`/tasks/${original.id}`, changes)
          return data.txid
        }),
      )

      const filteredTxids = txids.filter(
        (txid): txid is number => txid !== null && txid !== undefined,
      )

      if (filteredTxids.length === 0) {
        throw new Error('No valid txids returned from mutations')
      }

      const firstTxid = filteredTxids[0]
      if (firstTxid === undefined) {
        throw new Error('First txid is unexpectedly undefined')
      }

      return {
        txid: filteredTxids.length === 1 ? firstTxid : filteredTxids,
      }
    },
  })
}

// Not persisted — see issueHistoriesCollection (on-demand collections don't benefit from OPFS).
export const tasksCollection = createCollection(createTasksElectricOptions('tanstack-tasks'))
tasksCollection.createIndex((row) => row.issue_id)

export function createTaskHistoriesElectricOptions(id: string) {
  return electricCollectionOptions({
    ...autoIndexOptions,
    id,
    // Detail-only: the inbox task list only needs histories for the selected issue's tasks.
    // On-demand lets the pushed-down `task_id in (...)` predicate load that small subset instead
    // of hydrating and sorting the full task history table when a task-bearing issue is selected.
    syncMode: 'on-demand',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'task_histories',
        columns: [
          'id',
          'task_id',
          'actor_id',
          'actor_type',
          'integration_name',
          'automation_id',
          'event_type',
          'from_status',
          'to_status',
          'created_at',
          'updated_at',
        ],
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
      },
    },
    getKey: (item) => item.id,
    schema: TaskHistorySchema,
  })
}

export const taskHistoriesCollection = createCollection(
  createTaskHistoriesElectricOptions('tanstack-task-histories'),
)

const AutomationSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  is_active: z.boolean(),
  created_by_id: z.number().nullable(),
  last_published_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

export type Automation = z.infer<typeof AutomationSchema>

export const automationsCollection = createCollection(
  electricCollectionOptions({
    ...autoIndexOptions,
    id: 'tanstack-automations',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'automations',
        columns: [
          'id',
          'name',
          'description',
          'is_active',
          'created_by_id',
          'last_published_at',
          'created_at',
          'updated_at',
        ],
        where: 'deleted_at IS NULL',
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
      },
    },
    getKey: (item) => item.id,
    schema: AutomationSchema,
  }),
)

export const automationExecutionsCollection = createCollection(
  electricCollectionOptions({
    ...autoIndexOptions,
    id: 'tanstack-automation-executions',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'automation_executions',
        columns: [
          'id',
          'automation_id',
          'issue_id',
          'triggered_by_user_id',
          'trigger_type',
          'trigger_context',
          'status',
          'error_message',
          'started_at',
          'completed_at',
          'created_at',
          'updated_at',
        ],
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
      },
    },
    getKey: (item) => item.id,
    schema: AutomationExecutionSchema,
  }),
)

export const externalIssueTypesCollection = createCollection(
  queryCollectionOptions({
    ...autoIndexOptions,
    queryKey: ['tanstack-api', 'external_issue_types'],
    queryFn: () => fetchTanstackApi<ExternalIssueType[]>('external_issue_types'),
    queryClient,
    getKey: (item) => item.id,
    schema: ExternalIssueTypeSchema,
    refetchInterval: 1000 * 60 * 60, // 60 minutes
  }),
)

export const externalIssuesCollection = createCollection(
  electricCollectionOptions({
    ...autoIndexOptions,
    id: 'tanstack-external-issues',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'external_issues',
        columns: [
          'id',
          'external_id',
          'external_issue_type_id',
          'location',
          'description',
          'state',
          'image_urls',
          'reported_at',
        ],
        where: `reported_at >= '${formatISO(startOfMonth(subMonths(new Date(), 3)))}'`,
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
        // @ts-expect-error Type mismatch with geometry parser return type
        geometry: (value) => parseEwkb(value),
      },
    },
    getKey: (item) => item.id,
    schema: ExternalIssueSchema,
  }),
)

export function createDatafleetIssuesElectricOptions(id: string) {
  return electricCollectionOptions({
    ...autoIndexOptions,
    id,
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'datafleet_issues',
        columns: [
          'id',
          'image_id',
          'detected_at',
          'category',
          'status',
          'vehicle_license_plate',
          'related_issue_id',
          'datafleet_syncer_id',
          'created_at',
          'updated_at',
        ],
        where: 'related_issue_id IS NOT NULL',
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
      },
    },
    getKey: (item) => item.id,
    schema: DatafleetIssueSchema,
  })
}

export const datafleetIssuesCollection = createCollection(
  persisted(createDatafleetIssuesElectricOptions('tanstack-datafleet-issues')),
)

export const datafleetSyncersCollection = createCollection(
  queryCollectionOptions({
    ...autoIndexOptions,
    queryKey: ['tanstack-api', 'datafleet_syncers'],
    queryFn: () => fetchTanstackApi<DatafleetSyncer[]>('datafleet_syncers'),
    queryClient,
    getKey: (item) => item.id,
    schema: DatafleetSyncerSchema,
    refetchInterval: 1000 * 60 * 60, // 60 minutes
  }),
)

export const IssueViewSchema = z.object({
  id: z.number(),
  user_id: z.number(),
  team_id: z.number().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  definition: z.unknown(),
  created_at: z.string(),
  updated_at: z.string(),
})

export type IssueViewRow = z.infer<typeof IssueViewSchema>

/**
 * The rows a user may sync: their own views plus everything shared with one of their teams.
 * `ForwardAuthController` rebuilds this exact string from the user's team memberships and rejects
 * any other `where`, so the formatting here — sorted ids, `, ` separators — is load-bearing.
 */
export function issueViewsWhere(userId: number, teamIds: number[]): string {
  const own = `user_id = ${userId}`

  if (teamIds.length === 0) {
    return own
  }

  const sorted = [...teamIds].sort((left, right) => left - right)

  return `${own} OR team_id IN (${sorted.join(', ')})`
}

/**
 * Saved issue filters. The shape is per-user, so the collection is built lazily once the page props
 * name the current user, and memoized per `where` so every page shares one Electric stream.
 *
 * Deliberately not persisted to OPFS: it is a handful of rows read once per page, and adding a
 * persisted collection would force a `PERSISTED_SCHEMA_VERSION` bump that resyncs every user's
 * whole local store. Writes go through `IssueViewController` and reconcile via `awaitTxId`.
 */
const issueViewCollections = new Map<string, ReturnType<typeof createIssueViewsCollection>>()

function createIssueViewsCollection(where: string) {
  return createCollection(
    electricCollectionOptions({
      ...autoIndexOptions,
      id: `tanstack-issue-views-${where}`,
      shapeOptions: {
        url: getElectricUrl(),
        params: {
          table: 'issue_views',
          where,
          columns: [
            'id',
            'user_id',
            'team_id',
            'name',
            'description',
            'definition',
            'created_at',
            'updated_at',
          ],
        },
        parser: {
          int8: Number,
          timestamp: (value) => parseTimestamp(value),
        },
      },
      getKey: (item) => item.id,
      schema: IssueViewSchema,
    }),
  )
}

export function getIssueViewsCollection(userId: number, teamIds: number[]) {
  const where = issueViewsWhere(userId, teamIds)
  const existing = issueViewCollections.get(where)

  if (existing) {
    return existing
  }

  const collection = createIssueViewsCollection(where)
  issueViewCollections.set(where, collection)

  return collection
}

export const PresenceSchema = z.object({
  id: z.string(),
  user_id: z.number(),
  viewing_issue_id: z.number().nullable(),
  context: z.string(),
  last_seen_at: z.string(),
})

export type Presence = z.infer<typeof PresenceSchema>

/**
 * Inbox presence, backed by the `user_presences` Postgres table instead of websockets. Each tab
 * upserts a heartbeat row via `PresenceController`; this collection syncs every client's rows back.
 *
 * Electric exclusively owns these rows. Heartbeats and leaving use HTTP directly; immediate
 * self-presence stays local to useIssuePresence, outside the shared collection's query graph.
 *
 * Deliberately not persisted to OPFS: presence is ephemeral, so replaying stale rows on reload
 * would be wrong. Rows are filtered for staleness client-side; the server-side reaper handles
 * durable cleanup.
 */
export const presencesCollection = createCollection(
  electricCollectionOptions({
    ...autoIndexOptions,
    id: 'tanstack-user-presences',
    shapeOptions: {
      url: getElectricUrl(),
      params: {
        table: 'user_presences',
        where: "context = 'issue_inbox'",
        columns: ['id', 'user_id', 'viewing_issue_id', 'context', 'last_seen_at'],
      },
      parser: {
        int8: Number,
        timestamp: (value) => parseTimestamp(value),
      },
    },
    getKey: (item) => item.id,
    schema: PresenceSchema,
  }),
)

export function parseTimestamp(timestamp: string): string {
  return `${timestamp.replace(' ', 'T')}Z`
}

export function constructAddress(
  street?: string | null,
  houseNumber?: string | null,
  postalCode?: string | null,
  city?: string | null,
  includeCity = true,
): string | null {
  if (!street && !houseNumber && !postalCode && !city) {
    return null
  }

  const streetPart = street ? `${street}${houseNumber ? ` ${houseNumber}` : ''}` : ''

  if (!includeCity) {
    return streetPart || null
  }

  const cityPart = postalCode && city ? `${postalCode} ${city}` : (city ?? '')

  return [streetPart, cityPart].filter(Boolean).join(', ')
}

export function formatRelativeTime(dateString: string): string {
  try {
    return formatDistanceToNow(dateString, {
      addSuffix: true,
      locale: de,
    })
  } catch (error) {
    console.error('Error parsing date:', dateString, error)
    return 'unbekannt'
  }
}

export function createIssueTitle(typeName: string | null, description: string | null): string {
  if (!typeName && !description) {
    return 'Meldung'
  }

  let title = typeName ?? ''
  if (description) {
    title = title ? `${title}: ${description}` : description
  }

  const words = title.split(' ')
  if (words.length > 3) {
    return `${words.slice(0, 3).join(' ')}...`
  }

  return title || 'Meldung'
}

export function getFirstMedia(issue: Issue): MediaItem | null {
  const firstMedia = issue.media_json?.[0]
  return firstMedia ?? null
}

/**
 * Cleans up all active subscriptions when logging out.
 * Calls cleanup() on all collections to stop their sync and subscriptions.
 */
export function cleanupSubscriptions(): void {
  const collections = [
    issuesCollection,
    issueTypesCollection,
    zonesCollection,
    zoneGroupsCollection,
    vehiclesCollection,
    usersCollection,
    guestsCollection,
    issueHistoriesCollection,
    issueCommentsCollection,
    issueCommentTemplatesCollection,
    issueAttributesCollection,
    issueAttributeValuesCollection,
    tracemateTasksCollection,
    tracemateTasksWithLocationCollection,
    bulkyWasteAppointmentsCollection,
    bulkyWasteSettlementCollectionRoutes,
    issueForwardingsCollection,
    tasksCollection,
    taskHistoriesCollection,
    automationsCollection,
    automationExecutionsCollection,
    externalIssueTypesCollection,
    externalIssuesCollection,
    datafleetIssuesCollection,
    datafleetSyncersCollection,
    presencesCollection,
  ]

  for (const collection of collections) {
    try {
      void collection.cleanup()
    } catch (error) {
      // Silently fail if collections don't have cleanup method
      console.warn('Error cleaning up collection:', error)
    }
  }
}
